import {
	ComparisonOperator,
	CreditType,
	DepositHistoryPeriod,
	GameType,
	components,
	operations,
} from '@/schema'

export { ComparisonOperator, CreditType, DepositHistoryPeriod, GameType }

export type GetDepositPromotionRequest = {
	skip: number
	take: number
}

export type GetDepositPromotionResponse =
	components['schemas']['DepositPromotionsResponse']
export type GetDepositPromotionResponseData =
	components['schemas']['DepositPromotionResponse']

export type CreateDepositPromotionRequest =
	components['schemas']['CreateDepositPromotionDto']

export type CreateDepositPromotionResponse =
	components['schemas']['DepositPromotionResponse']

export type UpdateDepositPromotionRequest =
	components['schemas']['UpdateDepositPromotionDto']
export type UpdateDepositPromotionResponse =
	components['schemas']['DepositPromotionResponse']

export type GetDepositPromotionByIdResponse =
	components['schemas']['DepositPromotionResponse']

export enum PromotionType {
	NEW_USER = 'NEW_USER',
	FIRST_DEPOSIT = 'FIRST_DEPOSIT',
	FIRST_TIME_OF_DAY = 'FIRST_TIME_OF_DAY',
	DEPOSIT_AMOUNT = 'DEPOSIT_AMOUNT',
	DAY_OF_WEEK = 'DAY_OF_WEEK',
	LOSS_AMOUNT = 'LOSS_AMOUNT',
	TURNOVER = 'TURNOVER',
	RANK = 'RANK',
	DEPOSIT_HISTORY_AMOUNT = 'DEPOSIT_HISTORY_AMOUNT',
}

export enum DayOfWeek {
	MONDAY = 'MONDAY',
	TUESDAY = 'TUESDAY',
	WEDNESDAY = 'WEDNESDAY',
	THURSDAY = 'THURSDAY',
	FRIDAY = 'FRIDAY',
	SATURDAY = 'SATURDAY',
	SUNDAY = 'SUNDAY',
}

interface BasePromotionConstraint {
	type: PromotionType
}

export interface NewUserPromotionConstraint extends BasePromotionConstraint {
	type: PromotionType.NEW_USER
	isNewUser: boolean
}

export interface FirstDepositPromotionConstraint
	extends BasePromotionConstraint {
	type: PromotionType.FIRST_DEPOSIT
	isFirstDeposit: boolean
}

export interface FirstTimeOfDayPromotionConstraint
	extends BasePromotionConstraint {
	type: PromotionType.FIRST_TIME_OF_DAY
	isFirstTimeOfDay: boolean
}

export interface DepositAmountPromotionConstraint
	extends BasePromotionConstraint {
	type: PromotionType.DEPOSIT_AMOUNT
	operator: ComparisonOperator
	amount: number
}

export interface DayOfWeekPromotionConstraint extends BasePromotionConstraint {
	type: PromotionType.DAY_OF_WEEK
	days: DayOfWeek[]
}

export interface RankPromotionConstraint extends BasePromotionConstraint {
	type: PromotionType.RANK
	rank: string
}

export interface DepositHistoryAmountPromotionConstraint
	extends BasePromotionConstraint {
	type: PromotionType.DEPOSIT_HISTORY_AMOUNT
	period: DepositHistoryPeriod
	operator: ComparisonOperator
	amount: number
}

export type PromotionConstraint =
	| NewUserPromotionConstraint
	| FirstDepositPromotionConstraint
	| FirstTimeOfDayPromotionConstraint
	| DepositAmountPromotionConstraint
	| DayOfWeekPromotionConstraint
	| RankPromotionConstraint
	| DepositHistoryAmountPromotionConstraint

export interface DepositPromotionFormValues {
	name: string
	banner: string
	shortDescription: string
	description: string
	order: number
	isDisplayOnWebsite: boolean
	isActive: boolean
	creditType: CreditType
	creditAmount?: number
	creditRatio?: number
	maxCreditAmount?: number
	gameTypes: GameType[]
	turnoverRound: number
	minimumWithdrawCredit: number
	maximumWithdrawCredit: number
	limitQuota: number
	limitQuotaPerDay: number
	claimConstraint: PromotionConstraint[]
	hasExpiration: boolean
}

export type GetCommissionCashbackRequest = {
	skip: number
	take: number
}

export type GetCommissionCashbackResponse =
	components['schemas']['CommissionCashbacksResponse']
export type GetCommissionCashbackResponseData =
	components['schemas']['CommissionCashbackResponse']

export type CreateCommissionCashbackRequest =
	components['schemas']['CreateCommissionCashbackDto']
export type CreateCommissionCashbackResponse =
	components['schemas']['CommissionCashbackResponse']

export type UpdateCommissionCashbackRequest =
	components['schemas']['UpdateCommissionCashbackDto']
export type UpdateCommissionCashbackResponse =
	components['schemas']['CommissionCashbackResponse']

export type GetCommissionCashbackByIdResponse =
	components['schemas']['CommissionCashbackResponse']

export type GetReturnLossRequest = {
	skip: number
	take: number
}

export type GetReturnLossResponse =
	components['schemas']['ReturnLossesResponse']
export type GetReturnLossResponseData =
	components['schemas']['ReturnLossResponse']

export type CreateReturnLossRequest =
	components['schemas']['CreateReturnLossDto']
export type CreateReturnLossResponse =
	components['schemas']['ReturnLossResponse']

export type UpdateReturnLossRequest =
	components['schemas']['UpdateReturnLossDto']
export type UpdateReturnLossResponse =
	components['schemas']['ReturnLossResponse']

export type GetReturnLossByIdResponse =
	components['schemas']['ReturnLossResponse']

export type GetLuckyWheelResponse =
	components['schemas']['LuckyWheelSettingResponse'] & {
		remainingAttempts: number
	}

export type UpdateLuckyWheelRequest =
	components['schemas']['UpdateLuckyWheelSettingDto']
export type UpdateLuckyWheelResponse =
	components['schemas']['LuckyWheelSettingResponse']

export type GetInviteFriendResponse =
	components['schemas']['InviteFriendSettingResponse']

export type UpdateInviteFriendRequest =
	components['schemas']['UpdateInviteFriendSettingDto']
export type UpdateInviteFriendResponse =
	components['schemas']['InviteFriendSettingResponse']

export type CreateDailyCheckInRequest =
	components['schemas']['CreateDailyCheckInSettingDto']
export type CreateDailyCheckInResponse =
	components['schemas']['DailyCheckInSettingResponse']

export type GetDailyCheckInRequest = {
	skip: number
	take: number
}

export type GetDailyCheckInResponse =
	components['schemas']['DailyCheckInSettingsResponse']
export type GetDailyCheckInDataResponse =
	components['schemas']['DailyCheckInSettingResponse']

export type GetDailyCheckInByIdResponse =
	components['schemas']['DailyCheckInSettingResponse']

export type UpdateDailyCheckInRequest =
	components['schemas']['UpdateDailyCheckInSettingDto']
export type UpdateDailyCheckInResponse =
	components['schemas']['DailyCheckInSettingResponse']

export type GetDepositPromotionNamesForSelectorResponse =
	components['schemas']['PromotionSelectorListResponse']
export type GetDepositPromotionNamesForSelectorResponseData =
	components['schemas']['PromotionSelectorResponse']

export type GetCommissionNameResponse =
	components['schemas']['CommissionCashbackSelectorListResponse']
export type GetCommissionNameResponseData =
	components['schemas']['CommissionCashbackSelectorResponse']

export type GetReturnLossNameResponse =
	components['schemas']['ReturnLossSelectorListResponse']
export type GetReturnLossNameResponseData =
	components['schemas']['ReturnLossSelectorResponse']

export type GetDailyCheckInNameResponse =
	components['schemas']['DailyCheckInSelectorListResponse']
export type GetDailyCheckInNameResponseData =
	components['schemas']['DailyCheckInSelectorResponse']

export type SpinLuckyWheelResponse =
	components['schemas']['LuckyWheelSpinResponse']

export type GetPresetIdResponse = components['schemas']['GetPresetsResponse']

export type GetDepositPromotionNamesForSelectorCouponResponse =
	components['schemas']['PromotionSelectorListResponse']
export type GetDepositPromotionNamesForSelectorCouponResponseData =
	components['schemas']['PromotionSelectorResponse']

export type GetDailyPlayerReturnLossRequest =
	operations['ReturnLossController_getDailyPlayerReturnLoss']['parameters']['query']

export type GetDailyPlayerReturnLossResponse =
	components['schemas']['AdminDailyPlayerReturnLossListResponse']
export type GetDailyPlayerReturnLossResponseData =
	components['schemas']['AdminDailyPlayerReturnLossResponse']

export type GetReturnLossHistoryRequest =
	operations['ReturnLossController_getReturnLossHistory']['parameters']['query']

export type GetReturnLossHistoryResponse =
	components['schemas']['AdminReturnLossHistoryListResponse']
export type GetReturnLossHistoryResponseData =
	components['schemas']['AdminReturnLossHistoryResponse']

export { PathsApiAdminInviteFriendCommissionHistoryGetParametersQueryCommissionType as InviteFriendCommissionType } from '@/schema'

export type GetInviteFriendCommissionHistoryRequest =
	operations['InviteFriendController_getCommissionHistory']['parameters']['query']

export type GetInviteFriendCommissionHistoryResponse =
	components['schemas']['AdminInviteFriendCommissionHistoryListResponse']
export type GetInviteFriendCommissionHistoryResponseData =
	components['schemas']['AdminInviteFriendCommissionHistoryResponse']

// Deposit Promotion Presets (from schema)
export type GetDepositPromotionPresetsResponse =
	components['schemas']['DepositPromotionPresetListResponse']
export type DepositPromotionPreset =
	components['schemas']['DepositPromotionPresetModel']
export type ApplyDepositPromotionPresetRequest =
	components['schemas']['ApplyDepositPromotionPresetDto']
export type ApplyDepositPromotionPresetResponse =
	components['schemas']['ApplyDepositPromotionPresetResponse']

// Lucky Wheel Image Presets
export type LuckyWheelImagePreset =
	components['schemas']['AdminLuckyWheelImagePresetResponse']

export type GetLuckyWheelImagePresetsResponse =
	components['schemas']['AdminLuckyWheelImagePresetsResponse']
